/**
 * Integrytas Group — PFW Third-Party Portal: browser-side portal auth.
 *
 * COMPLETELY SEPARATE from the regular application login (GoTrue / useAuthSession).
 * A portal user is a pfw_portal_user (its own identity store), authenticated via the
 * gateway `portal_login` action, which returns a scoped JWT. That token is stored
 * apart from any GoTrue session and only ever used for portal data calls — it carries
 * no app identity, so it can never act as a csm_user_master user.
 */

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
const GATEWAY_URL       = `${SUPABASE_URL}/functions/v1/csm-api`;
const STORAGE_KEY       = 'pfw_portal_session';

export interface PortalSession {
  token: string;
  portal_user_id: string;
  pfw_company_id: number;
  company_code: string;
  display_name: string | null;
  company_role_id: number | null;
  /** epoch seconds when the token expires (best-effort, from expires_in). */
  expires_at: number;
}

export interface PortalLoginResult { session: PortalSession | null; error: string | null; }

/** Phase-2: verify credentials and obtain a scoped portal token. */
export async function portalLogin(company: string, username: string, password: string): Promise<PortalLoginResult> {
  try {
    const resp = await fetch(GATEWAY_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'x-csm-client': 'csm-web',
      },
      body: JSON.stringify({ kind: 'portal_login', company, username, password }),
    });
    const json = await resp.json().catch(() => ({ data: null, error: `PFW.PORTAL_BAD_RESPONSE_${resp.status}` }));
    if (!resp.ok || json.error || !json.data?.token) {
      return { session: null, error: json.error ?? `PFW.PORTAL_HTTP_${resp.status}` };
    }
    const d = json.data as { token: string; portal_user_id: string; pfw_company_id: number; company_code: string; display_name: string | null; company_role_id: number | null; expires_in: number };
    const session: PortalSession = {
      token: d.token, portal_user_id: d.portal_user_id, pfw_company_id: d.pfw_company_id,
      company_code: d.company_code, display_name: d.display_name, company_role_id: d.company_role_id,
      expires_at: Math.floor(Date.now() / 1000) + (d.expires_in ?? 0),
    };
    savePortalSession(session);
    return { session, error: null };
  } catch {
    return { session: null, error: 'PFW.PORTAL_NETWORK_ERROR' };
  }
}

export function savePortalSession(s: PortalSession): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

export function loadPortalSession(): PortalSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as PortalSession;
    if (s.expires_at && s.expires_at * 1000 < Date.now()) { clearPortalSession(); return null; }
    return s;
  } catch { return null; }
}

export function clearPortalSession(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
